import React, { useState } from "react";
import { Panel } from "../../components/ui/dashboard";

// Replace with your real API data
const MOCK_DRIVERS = [
  { id: "d1", name: "Ramesh Kumar" },
  { id: "d2", name: "Suresh Singh" },
  { id: "d3", name: "Ajay Verma" },
];

const MOCK_VEHICLES = [
  { id: "v1", label: "RJ14-GH-1234 — Tata 407" },
  { id: "v2", label: "RJ14-AB-5678 — Ashok Leyland" },
  { id: "v3", label: "RJ14-XY-9012 — Mahindra Bolero" },
];

interface Item {
  id: string;
  name: string;
  quantity: string;
  weight: string;
  ratePerQtl: string;
}

const emptyItem = (): Item => ({
  id: Math.random().toString(36).slice(2),
  name: "",
  quantity: "",
  weight: "",
  ratePerQtl: "",
});

const CreateConsignment: React.FC = () => {
  const [step, setStep] = useState(1);

  // Step 1 — Transport
  const [driverId, setDriverId] = useState("");
  const [vehicleId, setVehicleId] = useState("");

  // Step 3 — Items
  const [items, setItems] = useState<Item[]>([emptyItem()]);
  const [hasBroker, setHasBroker] = useState(false);
  const [brokerPaidBy, setBrokerPaidBy] = useState<"receiver" | "sender" | null>(null);

  const updateItem = (id: string, field: keyof Item, value: string) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value } : it)));

  const addItem = () => setItems((prev) => [...prev, emptyItem()]);

  const removeItem = (id: string) => {
    if (items.length === 1) return;
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const itemTotal = (it: Item) =>
    (parseFloat(it.quantity) || 0) * (parseFloat(it.ratePerQtl) || 0);

  const grandTotal = items.reduce((acc, it) => acc + itemTotal(it), 0);

  return (
    <div className="p-4 md:p-6 space-y-4">
      <Panel title="Create Consignment" subtitle="Transport details → Receiver details → Item list → Payment mode">
        <div className="flex gap-2 mb-4 flex-wrap">
          {[
            { n: 1, label: "Transport" },
            { n: 2, label: "Receiver" },
            { n: 3, label: "Items" },
            { n: 4, label: "Payment" },
          ].map((s) => (
            <button
              key={s.n}
              onClick={() => setStep(s.n)}
              className={`px-3 py-1 text-xs rounded-lg ${
                step === s.n ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              Step {s.n}: {s.label}
            </button>
          ))}
        </div>

        {/* ── STEP 1: Transport (new) ── */}
        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Driver</label>
              <select
                className="border rounded-lg px-3 py-2 text-sm w-full"
                value={driverId}
                onChange={(e) => setDriverId(e.target.value)}
              >
                <option value="">— Select Driver —</option>
                {MOCK_DRIVERS.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Vehicle</label>
              <select
                className="border rounded-lg px-3 py-2 text-sm w-full"
                value={vehicleId}
                onChange={(e) => setVehicleId(e.target.value)}
              >
                <option value="">— Select Vehicle —</option>
                {MOCK_VEHICLES.map((v) => (
                  <option key={v.id} value={v.id}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* ── STEP 2: Receiver (original step 1, unchanged) ── */}
        {step === 2 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Receiver Name" />
            <input className="border rounded-lg px-3 py-2 text-sm" placeholder="Receiver Mobile" />
            <textarea className="border rounded-lg px-3 py-2 text-sm md:col-span-2" placeholder="Pickup Address" />
            <textarea className="border rounded-lg px-3 py-2 text-sm md:col-span-2" placeholder="Delivery Address" />
          </div>
        )}

        {/* ── STEP 3: Items (original step 2, reworked) ── */}
        {step === 3 && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-slate-900">Item Entry</p>

            {/* Column headers — desktop */}
            <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_auto] gap-3 text-[10px] font-semibold uppercase text-slate-400 px-1">
              <span>Item Name</span>
              <span>Qty (Qtl)</span>
              <span>Weight (Qtl)</span>
              <span>Rate / Qtl (₹)</span>
            </div>

            {items.map((it) => (
              <div key={it.id}>
                <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_auto] gap-3 items-center">
                  <input
                    className="border rounded-lg px-3 py-2 text-sm"
                    placeholder="Item Name"
                    value={it.name}
                    onChange={(e) => updateItem(it.id, "name", e.target.value)}
                  />
                  {/* Qty — fixed unit: Quintal */}
                  <input
                    className="border rounded-lg px-3 py-2 text-sm"
                    placeholder="Qty (Qtl)"
                    type="number"
                    min="0"
                    value={it.quantity}
                    onChange={(e) => updateItem(it.id, "quantity", e.target.value)}
                  />
                  {/* Weight — fixed unit: Quintal */}
                  <input
                    className="border rounded-lg px-3 py-2 text-sm"
                    placeholder="Weight (Qtl)"
                    type="number"
                    min="0"
                    value={it.weight}
                    onChange={(e) => updateItem(it.id, "weight", e.target.value)}
                  />
                  {/* Declared value is per Quintal; total = qty × rate */}
                  <input
                    className="border rounded-lg px-3 py-2 text-sm"
                    placeholder="₹ / Qtl"
                    type="number"
                    min="0"
                    value={it.ratePerQtl}
                    onChange={(e) => updateItem(it.id, "ratePerQtl", e.target.value)}
                  />
                  <button
                    onClick={() => removeItem(it.id)}
                    disabled={items.length === 1}
                    className="text-slate-400 hover:text-red-500 disabled:opacity-20 text-xl leading-none transition-colors"
                    title="Remove item"
                  >
                    ×
                  </button>
                </div>

                {/* Row subtotal: qty × rate */}
                {parseFloat(it.quantity) > 0 && parseFloat(it.ratePerQtl) > 0 && (
                  <div className="text-right text-xs text-slate-500 mt-1 pr-6">
                    {parseFloat(it.quantity)} Qtl × ₹{parseFloat(it.ratePerQtl).toLocaleString()}
                    {" = "}
                    <span className="font-semibold text-slate-700">
                      ₹{itemTotal(it).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            ))}

            <button
              onClick={addItem}
              className="text-xs px-3 py-1 rounded-lg bg-slate-100 text-slate-700"
            >
              + Add Another Item
            </button>

            {/* Grand total */}
            {grandTotal > 0 && (
              <div className="flex justify-end">
                <div className="text-xs bg-slate-900 text-white rounded-lg px-4 py-2 font-medium">
                  Total Declared Value: ₹{grandTotal.toLocaleString()}
                </div>
              </div>
            )}

            {/* Broker */}
            <div className="border-t border-slate-100 pt-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer w-fit">
                <input
                  type="checkbox"
                  checked={hasBroker}
                  onChange={(e) => {
                    setHasBroker(e.target.checked);
                    if (!e.target.checked) setBrokerPaidBy(null);
                  }}
                  className="w-4 h-4 accent-slate-800 cursor-pointer"
                />
                <span className="text-sm text-slate-700">Is there a broker involved?</span>
              </label>

              {hasBroker && (
                <div className="ml-6 space-y-1.5">
                  <p className="text-xs text-slate-500">Broker commission paid by:</p>
                  <div className="flex gap-3">
                    {(["receiver", "sender"] as const).map((party) => (
                      <label
                        key={party}
                        className={`flex items-center gap-2 px-4 py-1.5 rounded-lg border text-sm cursor-pointer transition-all ${
                          brokerPaidBy === party
                            ? "bg-slate-900 border-slate-900 text-white"
                            : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                        }`}
                      >
                        <input
                          type="radio"
                          name="brokerPaidBy"
                          value={party}
                          checked={brokerPaidBy === party}
                          onChange={() => setBrokerPaidBy(party)}
                          className="hidden"
                        />
                        {party.charAt(0).toUpperCase() + party.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── STEP 4: Payment (original step 3, unchanged) ── */}
        {step === 4 && (
          <div className="space-y-3">
            <select className="border rounded-lg px-3 py-2 text-sm w-full md:w-1/2">
              <option>paid</option>
              <option>to_pay</option>
              <option>credit</option>
            </select>
            <div className="text-xs text-slate-500">LR Number will be auto-generated on save.</div>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button className="px-3 py-2 text-sm rounded-lg bg-slate-100 text-slate-700">Save Draft</button>
          <button className="px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white">Create Consignment</button>
        </div>
      </Panel>
    </div>
  );
};

export default CreateConsignment;